import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ButtonBase } from '@material-ui/core';
import { Spinner, Tables } from '../../../../../../Components';
import { TableActions } from '../../../../../../Enums';
import {
  getDownloadableLink,
  getMessageFromErrorResponse,
  GetParams,
  showError,
} from '../../../../../../Helper';
import { GetAllVendorDocuments } from '../../../../../../Services';
import { VendorProfileDocumentManagementDialog } from './Dialogs';
import { DeleteDocumentDialog } from '../../../../UnitsView/UnitsProfileManagementView/Sections/UnitProfileDocumentsComponent/DeleteDocumentDialog';
import { useLocation } from 'react-router-dom';
import { headerData } from './headerData';
import { isEqual } from 'lodash';

export const VendorDocuments = ({ parentTranslationPath }) => {
  const { t } = useTranslation(parentTranslationPath);
  const location = useLocation();
  const [files, setFiles] = useState([]);
  const [filter, setFilter] = useState({
    pageSize: 25,
    pageIndex: 0,
  });
  const [isLoading, setIsLoading] = useState({
    allVendorDocuments: false,
    vendorDocumentInfo: false,
    addVendorDocuments: false,
    editVendorDocuments: false,
    info: false,
  });

  const [activeItem, setActiveItem] = useState(null);
  const [tableActions, setTableActions] = useState([]);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [response, setResponse] = useState({ result: [], totalCount: 0 });
  const vendorId = useMemo(() => +GetParams('id'), [location]);
  const [isOpenManagementDialog, setIsOpenManagementDialog] = useState(false);

  const onPageIndexChanged = (pageIndex) => {
    setFilter((item) => ({ ...item, pageIndex }));
  };
  const onPageSizeChanged = (pageSize) => {
    setFilter((item) => ({ ...item, pageIndex: 0, pageSize }));
  };

  const GetVendorDocuments = useCallback(async () => {
    try {
      setIsLoading((item) => ({ ...item, allVendorDocuments: true }));
      const { result, totalCount } = await GetAllVendorDocuments({
        ...filter,
        pageIndex: filter.pageIndex + 1,
        vendorId: vendorId,
      });
      if (result) {
        setResponse({
          result: result,
          totalCount: totalCount,
        });
      }
    } catch (error) {
      showError(t('something_went_wrong'));
    } finally {
      setIsLoading((item) => ({ ...item, allVendorDocuments: false }));
    }
  }, [filter]);

  const focusedRowChanged = (rowIndex, item) => {
    if (rowIndex !== -1) {
      let newItems = [];
      const defaultTableActions = getDefaultActionsWithPermissions();

      newItems.push({
        enum: TableActions.downloadText.key,
        isDisabled: false,
        externalComponent: null,
      });
      setTableActions([...defaultTableActions, ...newItems]);
    }
  };

  const getDefaultActionsWithPermissions = () => {
    const list = [];
    // if (
    //   returnPropsByPermissions(
    //     DevelopersPermissions.EditDocuments.permissionsId
    //   )
    // )
    list.push({
      enum: TableActions.editText.key,
      isDisabled: false,
      externalComponent: null,
    });

    // if (
    //   returnPropsByPermissions(
    //     DevelopersPermissions.DeletDocuments.permissionsId
    //   )
    // )
    list.push({
      enum: TableActions.deleteText.key,
      isDisabled: false,
      externalComponent: null,
    });

    return list;
  };
  const tableActionClicked = useCallback(
    (actionEnum, item) => {
      let documents = [];
      setActiveItem({
        documentName: item.documentName,
        fileName: item.fileName,
        documentRemarks: item.documentRemarks,
        categoryId: item.documentCategoryId,
        categoryName: item.documentCategory,
        fileId: item.fileId,
        vendorDocumentId: item.vendorDocumentId,
        vendorId,
      });

      if (item.documentName) {
        documents = response.result.filter(
          (f) => f.documentName === item.documentName
        );
      }
      setFiles([...documents]);

      if (actionEnum === TableActions.editText.key)
        setIsOpenManagementDialog(true);

      if (actionEnum === TableActions.deleteText.key)
        setIsOpenDeleteDialog(true);

      if (actionEnum === TableActions.downloadText.key) {
        try {
          const link = document.createElement('a');
          link.setAttribute('download', item.fileName || item.documentName);
          link.href = getDownloadableLink(
            item.uuid || item.documentId || item.fileId
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
          setActiveItem(null);
        } catch (error) {}
      }
    },
    [response]
  );

  const reloadData = () => {
    setFilter((item) => ({ ...item, pageIndex: 0 }));
  };

  const onFilterValuesChanged = (filterValue) => {
    if (filterValue && !!Object.values(filterValue).length) {
      const { value, searchableKey } = Object.values(filterValue)[0];
      setFilter((filter) => ({
        ...filter,
        [searchableKey]: value,
      }));
    } else {
      const updatedFilter = Object.fromEntries(
        Object.entries(filter).filter(([_key, value]) => !!value || value === 0)
      );
      if (!isEqual(updatedFilter, filter)) {
        setFilter(updatedFilter);
      }
    }
  };

  useEffect(() => {
    GetVendorDocuments();
  }, [GetVendorDocuments]);

  useEffect(() => {
    setTableActions(getDefaultActionsWithPermissions());
  }, []);

  return (
    <div className='associated-contacts-wrapper childs-wrapper'>
      <Spinner
        isActive={
          isLoading.addVendorDocuments ||
          isLoading.allVendorDocuments ||
          isLoading.editVendorDocuments
        }
        isAbsolute
      />
      <div className='title-section'>
        <span>{t(`vendor_documents`)}</span>
      </div>
      <div className='filter-section px-2 mb-3'>
        <div className='section'>
          <ButtonBase
            className='btns theme-solid px-3'
            onClick={() => {
              setIsOpenManagementDialog(true);
            }}
          >
            <span className='mdi mdi-plus' />
            {t(`add_vendor_document`)}
          </ButtonBase>
        </div>
      </div>
      <div className='w-100 px-2'>
        <Tables
          data={response.result}
          headerData={headerData}
          onPageIndexChanged={onPageIndexChanged}
          onPageSizeChanged={onPageSizeChanged}
          defaultActions={tableActions}
          focusedRowChanged={focusedRowChanged}
          actionsOptions={{
            onActionClicked: tableActionClicked,
          }}
          isWithFilter
          filterData={headerData.map((item) => ({
            ...item,
            searchableKey: item.input,
          }))}
          itemsPerPage={filter.pageSize}
          activePage={filter.pageIndex}
          parentTranslationPath={parentTranslationPath}
          totalItems={response.totalCount}
          onFilterValuesChanged={onFilterValuesChanged}
          FilterDisabledButton
        />
      </div>
      {isOpenManagementDialog && (
        <VendorProfileDocumentManagementDialog
          vendorId={vendorId}
          activeItem={activeItem}
          vendorFiles={files}
          isOpen
          isOpenChanged={() => {
            setIsOpenManagementDialog(false);
            setActiveItem(null);
          }}
          isVendorContact
          parentTranslationPath={parentTranslationPath}
          onSave={() => {
            setIsOpenManagementDialog(false);
            setActiveItem(null);
            reloadData();
          }}
        />
      )}
      {isOpenDeleteDialog && (
        <DeleteDocumentDialog
          activeItem={activeItem}
          open
          close={() => {
            setIsOpenDeleteDialog(false);
            setActiveItem(null);
          }}
          onSave={() => {
            setIsOpenDeleteDialog(false);
            setActiveItem(null);
            reloadData();
          }}
        />
      )}
    </div>
  );
};
VendorDocuments.propTypes = {
  parentTranslationPath: PropTypes.string.isRequired,
};
